import type Step from 'components/core/createModify/interfaces/step';
import type { StepFields } from 'components/core/createModify/interfaces/stepFields';
import type Tier from 'components/core/createModify/interfaces/tier';

import { isFieldsResolver } from 'components/core/createModify/helpers/stepResolvers';

const fieldMatchesActiveField = (fields: StepFields, activeField: string) =>
  Object.entries(fields).some(
    ([key, field]) =>
      key === activeField || field?.queryAlias === activeField || field?.queryAlias?.includes(activeField)
  );

const applyExpandBuilderConfig = (tier: Tier | undefined, field: StepFields[string] | undefined): void => {
  if (!(tier && tier.mode === 'modify' && field?.shouldExpandBuilderOnEdit)) {
    return;
  }

  tier.isExpanded = true;
  tier.shouldAutoExpand = true;
};

const findStepInResolverFields = (
  step: Step<any, any, any>,
  activeField: string,
  tier: Tier
): Step<any, any, any> | undefined => {
  if (typeof step.fields !== 'function') {
    return undefined;
  }

  const resolvedFields = step.fields({ tier });

  if (resolvedFields && fieldMatchesActiveField(resolvedFields, activeField)) {
    applyExpandBuilderConfig(tier, resolvedFields[activeField]);

    return step;
  }

  return undefined;
};

const findStepInStaticFields = (
  step: Step<any, any, any>,
  activeField: string,
  tier: Tier | undefined
): Step<any, any, any> | undefined => {
  const fields = step.fields as StepFields;

  if (fieldMatchesActiveField(fields, activeField)) {
    applyExpandBuilderConfig(tier, fields[activeField]);

    return step;
  }

  return undefined;
};

export const searchStepForActiveField = (
  step: Step<any, any, any>,
  activeField: string,
  tier: Tier | undefined
): Step<any, any, any> | undefined => {
  if (!step.fields) {
    return undefined;
  }

  if (isFieldsResolver(step.fields)) {
    return tier ? findStepInResolverFields(step, activeField, tier) : undefined;
  }

  return findStepInStaticFields(step, activeField, tier);
};
